import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Receipt, Edit2, X, Save, Trash2, Plus } from "lucide-react";

const STATUSES = ["pending", "confirmed", "preparing", "delivered", "cancelled"];
const STATUS_LABEL = {
  pending: "En attente",
  confirmed: "Confirmée",
  preparing: "En préparation",
  delivered: "Livrée",
  cancelled: "Annulée",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="ff-overline">Tableau de bord</p>
          <h1 className="font-serif text-3xl mt-2">Commandes ({orders.length})</h1>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="ff-input w-auto"
          data-testid="admin-orders-filter"
        >
          <option value="all">Tous les statuts</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
        </select>
      </div>

      {loading ? (
        <p className="mt-10 text-[#8A9A8E]">Chargement...</p>
      ) : (
        <div className="mt-8 bg-white border border-[#E5E3DC] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFEB]">
              <tr className="text-left">
                <th className="p-3">N°</th>
                <th className="p-3">Client</th>
                <th className="p-3">Date</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3">Statut</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.id} className="border-t border-[#F2EFEB]" data-testid={`admin-order-row-${o.id}`}>
                  <td className="p-3 font-mono text-xs">{o.order_number}</td>
                  <td className="p-3">
                    <div className="font-medium">{o.user_snapshot.company_name}</div>
                    <div className="text-xs text-[#8A9A8E]">{o.user_snapshot.email}</div>
                  </td>
                  <td className="p-3 text-[#4A5D4E]">{o.created_at?.slice(0, 10)}</td>
                  <td className="p-3 text-right font-medium">CHF {o.total.toFixed(2)}</td>
                  <td className="p-3">
                    <select
                      value={o.status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className="text-xs border border-[#E5E3DC] px-2 py-1 bg-white"
                      data-testid={`admin-order-status-${o.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex gap-1">
                      <a href={`${API}/admin/orders/${o.id}/invoice.pdf`} target="_blank" rel="noreferrer" className="p-2 hover:bg-[#F2EFEB] text-[#2D6A4F]" title="Facture" data-testid={`admin-invoice-${o.id}`}>
                        <FileDown className="w-4 h-4" />
                      </a>
                      <a href={`${API}/admin/orders/${o.id}/receipt.pdf`} target="_blank" rel="noreferrer" className="p-2 hover:bg-[#F2EFEB] text-[#E76F51]" title="Reçu" data-testid={`admin-receipt-${o.id}`}>
                        <Receipt className="w-4 h-4" />
                      </a>
                      <button onClick={() => setEditing(o)} className="p-2 hover:bg-[#F2EFEB] text-[#1A2B1E]" data-testid={`admin-edit-order-${o.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <EditOrderModal order={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EditOrderModal({ order, onClose, onSaved }) {
  const [items, setItems] = useState(order.items.map((i) => ({ ...i })));
  const [discount, setDiscount] = useState(order.discount_amount || 0);
  const [discountLabel, setDiscountLabel] = useState(order.discount_label || "Remise commerciale");
  const [notes, setNotes] = useState(order.notes || "");
  const [saving, setSaving] = useState(false);

  const subtotal = items.reduce((a, i) => a + i.price * Number(i.quantity || 0), 0);
  const total = Math.max(subtotal - Number(discount || 0), 0);
  const vat = total * 0.081 / 1.081;

  const updateItem = (idx, key, val) => {
    setItems((arr) => arr.map((i, k) => (k === idx ? { ...i, [key]: key === "name" || key === "unit" ? val : Number(val) } : i)));
  };
  const removeItem = (idx) => setItems((arr) => arr.filter((_, k) => k !== idx));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/orders/${order.id}`, {
        items, discount_amount: Number(discount), discount_label: discountLabel, notes,
      });
      toast.success("Commande mise à jour");
      onSaved();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-order-modal">
      <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E3DC] flex items-center justify-between">
          <h2 className="font-serif text-2xl">Modifier {order.order_number}</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F2EFEB]" data-testid="edit-order-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <p className="ff-overline mb-3">Articles</p>
            <table className="w-full text-sm">
              <thead className="text-left text-[#8A9A8E]">
                <tr>
                  <th>Produit</th>
                  <th className="text-right">Prix</th>
                  <th className="text-right">Qté</th>
                  <th className="text-right">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((i, idx) => (
                  <tr key={idx} className="border-t border-[#F2EFEB]">
                    <td className="py-2 pr-2">{i.name}<div className="text-xs text-[#8A9A8E]">{i.unit}</div></td>
                    <td><input type="number" step="0.01" value={i.price} onChange={(e) => updateItem(idx, "price", e.target.value)} className="ff-input w-24 text-right" data-testid={`edit-price-${idx}`} /></td>
                    <td><input type="number" step="0.5" value={i.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} className="ff-input w-20 text-right" data-testid={`edit-qty-${idx}`} /></td>
                    <td className="text-right pl-2">CHF {(i.price * i.quantity).toFixed(2)}</td>
                    <td><button onClick={() => removeItem(idx)} className="p-2 text-[#D62828]" data-testid={`edit-remove-${idx}`}><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Libellé remise</label>
              <input type="text" value={discountLabel} onChange={(e) => setDiscountLabel(e.target.value)} className="ff-input mt-1" data-testid="edit-discount-label" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Montant remise (CHF)</label>
              <input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="ff-input mt-1" data-testid="edit-discount-amount" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#4A5D4E]">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ff-input mt-1" data-testid="edit-notes" />
          </div>

          <div className="bg-[#F2EFEB] p-4 text-sm space-y-1">
            <div className="flex justify-between"><span>Sous-total</span><span>CHF {subtotal.toFixed(2)}</span></div>
            {Number(discount) > 0 && <div className="flex justify-between text-[#E76F51]"><span>{discountLabel}</span><span>- CHF {Number(discount).toFixed(2)}</span></div>}
            <div className="flex justify-between font-medium text-lg pt-2 border-t border-[#E5E3DC] mt-2"><span>Total TTC</span><span className="font-serif text-2xl text-[#2D6A4F]">CHF {total.toFixed(2)}</span></div>
            <div className="flex justify-between text-xs text-[#8A9A8E]"><span>dont TVA 8.1% incluse</span><span>CHF {vat.toFixed(2)}</span></div>
          </div>
        </div>
        <div className="p-6 border-t border-[#E5E3DC] flex justify-end gap-3">
          <button onClick={onClose} className="ff-btn-secondary" data-testid="edit-order-cancel">Annuler</button>
          <button onClick={save} disabled={saving} className="ff-btn-primary" data-testid="edit-order-save">
            <Save className="w-4 h-4" /> {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
